import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { v4 as uuidv4 } from "uuid";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpDirtyIndicatorArrayIcon } from "../../../../gvc/testNewForm/CpDirtyIndicatorArrayIconLink";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";

interface Props {}
export const HetznerProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");
  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const region = useCpFormValue(`hetzner.region`);
  const networkId = useCpFormValue(`hetzner.networkId`);
  const firewallId = useCpFormValue(`hetzner.firewallId`);
  const sshKey = useCpFormValue(`hetzner.sshKey`);
  const image = useCpFormValue(`hetzner.image`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Hetzner Provider
            <CpErrorWarningArrayIcon
              names={[
                `hetzner.region`,
                `hetzner.hetznerLabels`,
                `hetzner.networking`,
                `hetzner.preInstallScript`,
                `hetzner.tokenSecretLink`,
                `hetzner.networkId`,
                `hetzner.firewallId`,
                `hetzner.image`,
                `hetzner.sshKey`,
                `hetzner.autoscaler`,
                `hetzner.floatingIPSelector`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabel name={`hetzner.region`}>Region</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.region`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("region")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.region`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`hetzner.tokenSecretLink`}>Hetzner Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Hetzner token, it will be used to manage servers in your Hetzner account`,
                  }}
                />
              </CpLabelTooltip>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`hetzner.tokenSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-hetzner-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `hetzner.tokenSecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.tokenSecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`hetzner.networkId`}>Network ID</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.networkId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("networkId")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.networkId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`hetzner.image`}>Image</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.image`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("image")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.image`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`hetzner.firewallId`}>Firewall ID</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.firewallId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("firewallId")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.firewallId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`hetzner.sshKey`}>SSH Key</CpLabel>
              <Space.Compact>
                <CpInput name={`hetzner.sshKey`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("sshKey")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`hetzner.sshKey`} />
            </CpFormItem>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-center gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                    <CpDirtyIndicatorArrayIcon names={["provider-hetzner-advanced"]} />
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label={`Floating IP Selector`}
                  name={`hetzner.floatingIPSelector`}
                  labels={["Key", "Value"]}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label={`Hetzner Labels`}
                  name={`hetzner.hetznerLabels`}
                  labels={["Key", "Value"]}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <ProviderPreInstallScript provider="hetzner" />
              <ProviderNetworking provider="hetzner" />
              <ProviderAutoscaler provider="hetzner" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "region" ? (
        <NGProviderBrowser
          title="Browse Hetzner Regions"
          initialValue={region}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "locations",
                cluster: inputToMk8s(values),
              },
            });
            return data.locations;
          }}
          onOk={(value) => {
            formSetValue(id, `hetzner.region`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Location"]}
          getData={(item: any) => [item.name, item.name, `${item.city}, ${item.country}`]}
        />
      ) : null}
      {browserKey === "networkId" ? (
        <NGProviderBrowser
          title="Browse Hetzner Networks"
          initialValue={networkId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "networks",
                cluster: inputToMk8s(values),
              },
            });
            return data.networks;
          }}
          onOk={(value) => {
            formSetValue(id, `hetzner.networkId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id)]}
        />
      ) : null}
      {browserKey === "firewallId" ? (
        <NGProviderBrowser
          title="Browse Hetzner Firewalls"
          initialValue={firewallId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "firewalls",
                cluster: inputToMk8s(values),
              },
            });
            return data.firewalls;
          }}
          onOk={(value) => {
            formSetValue(id, `hetzner.firewallId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id)]}
        />
      ) : null}
      {browserKey === "image" ? (
        <NGProviderBrowser
          title="Browse Hetzner Images"
          initialValue={image}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "images",
                cluster: inputToMk8s(values),
              },
            });
            const _images = data.images.sort((a: any, b: any) => {
              if (a.name > b.name) {
                return 1;
              }
              if (a.name < b.name) {
                return -1;
              }
              return 0;
            });

            const images: any[] = [];

            for (const image of _images) {
              const foundImage = images.find((i) => i.name === image.name);
              if (foundImage) {
                if (!foundImage.architecture.includes(image.architecture)) {
                  foundImage.architecture.push(image.architecture);
                }
              } else {
                image.architecture = [image.architecture];
                images.push(image);
              }
            }
            return images;
          }}
          onOk={(value) => {
            formSetValue(id, `hetzner.image`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description", "Disk Size", "Architecture"]}
          getData={(item: any) => [
            item.name,
            item.name,
            item.description,
            formatSizeForProviderBrowser(item.disk_size),
            item.architecture.join(", "),
          ]}
        />
      ) : null}
      {browserKey === "sshKey" ? (
        <NGProviderBrowser
          title="Browse Hetzner SSH Keys"
          initialValue={sshKey}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ssh_keys",
                cluster: inputToMk8s(values),
              },
            });
            return data.ssh_keys;
          }}
          onOk={(value) => {
            formSetValue(id, `hetzner.sshKey`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "ID"]}
          getData={(item: any) => [String(item.id), item.name, String(item.id)]}
        />
      ) : null}
    </>
  );
};
